import React, { useCallback, useMemo, useRef, useState } from "react";
import { usePreferences } from "../context/PreferencesContext";
import { getAvaaioUiCopy } from "../utils/avaaioUiCopy";

export interface ChatHistoryMessage {
  id: string;
  sender: "user" | "ava";
  text: string;
  source?: "brain" | "openai" | "error";
  ts: number;
}

export default function ChatHistoryPanel({
  open,
  messages,
  isPhone = false,
  themeColors,
  onClose,
  onClearAll,
}: {
  open: boolean;
  messages: ChatHistoryMessage[];
  isPhone?: boolean;
  themeColors: any;
  onClose: () => void;
  onClearAll: () => void;
  }) {
  const { preferences } = usePreferences();
  const interfaceCopy = getAvaaioUiCopy(
    preferences.language
  );
  const historyCopy = interfaceCopy.history;

  const [scrollbarVisible, setScrollbarVisible] = useState(false);
  const [query, setQuery] = useState("");
  const timerRef = useRef<number | null>(null);

  const resetScrollbarFade = useCallback((delayMs = 4500) => {
    setScrollbarVisible(true);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setScrollbarVisible(false), delayMs);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return messages;
    return messages.filter((m) => `${m.sender} ${m.text}`.toLowerCase().includes(q));
  }, [messages, query]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-label={historyCopy.ariaLabel}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background:
          "radial-gradient(circle at 50% 0%, rgba(0,255,255,0.16), transparent 34%), rgba(0,0,0,0.68)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isPhone ? "12px" : "24px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "min(820px, 96vw)",
          maxHeight: "82vh",
          borderRadius: 26,
          border: `1px solid ${themeColors.border}`,
          background:
            "linear-gradient(180deg, rgba(4,14,28,0.96), rgba(1,4,10,0.985))",
          boxShadow:
            `0 0 28px ${themeColors.accentGlow}, 0 0 80px rgba(0,255,255,0.10), 0 26px 90px rgba(0,0,0,0.78)`,
          overflow: "hidden",
          color: themeColors.text,
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background:
              "linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(180deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
            backgroundSize: "32px 32px",
            opacity: 0.26,
          }}
        />

        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "16px 18px",
            borderBottom: `1px solid ${themeColors.border}`,
            background: "rgba(0,255,255,0.045)",
          }}
        >
          <div>
            <div style={{ fontWeight: 950, letterSpacing: "0.16em", textTransform: "uppercase" }}>
                              {historyCopy.title}
              </div>
              <div style={{ fontSize: 12, opacity: 0.72, marginTop: 5 }}>
                {historyCopy.sessionCount
                  .replace(
                    "{filtered}",
                    String(filtered.length)
                  )
                  .replace(
                    "{total}",
                    String(messages.length)
                  )}
              </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {messages.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  const confirmed = window.confirm(
                    "Delete all chat history? This cannot be undone."
                  );

                  if (!confirmed) return;

                  onClearAll();
                  setQuery("");
                }}
                style={{
                  minHeight: 36,
                  padding: "0 12px",
                  borderRadius: 999,
                  border: "1px solid rgba(255,90,90,0.48)",
                  background: "rgba(120,20,28,0.26)",
                  color: "#ffb8bd",
                  fontWeight: 850,
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                Clear All Chats
              </button>
            )}

            <button
              type="button"
              aria-label={historyCopy.close}
              title={historyCopy.close}
              onClick={onClose}
            style={{
              minWidth: 40,
              width: 40,
              height: 36,
              borderRadius: 999,
              border: `1px solid ${themeColors.border}`,
              background: "rgba(0,0,0,0.58)",
              color: themeColors.text,
              boxShadow: `0 0 14px ${themeColors.accentGlow}`,
              cursor: "pointer",
              fontSize: 20,
            }}
          >
            ×
          </button>
          </div>
        </div>

        <div style={{ position: "relative", padding: "12px 14px 0" }}>
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              resetScrollbarFade(5000);
            }}
                          placeholder={
                historyCopy.searchPlaceholder
              }
              style={{
              width: "100%",
              boxSizing: "border-box",
              borderRadius: 999,
              border: `1px solid ${themeColors.border}`,
              background: "rgba(0,0,0,0.45)",
              color: themeColors.text,
              outline: "none",
              padding: "11px 14px",
              fontSize: 13,
              boxShadow: `inset 0 0 18px rgba(0,255,255,0.05), 0 0 14px ${themeColors.accentGlow}`,
            }}
          />
        </div>

        <div
          className={scrollbarVisible ? "avaaio-scrollbar-visible" : "avaaio-scrollbar-hidden"}
          onWheel={() => resetScrollbarFade(4500)}
          onTouchStart={() => resetScrollbarFade(4500)}
          onMouseMove={() => resetScrollbarFade(4500)}
          style={{
            position: "relative",
            maxHeight: "calc(82vh - 126px)",
            overflowY: "auto",
            padding: "14px",
            scrollbarGutter: "stable",
          }}
        >
          {filtered.length === 0 ? (
                          <div style={{ opacity: 0.75, padding: "22px" }}>
                {historyCopy.noMatches}
              </div>
          ) : (
            filtered.map((item) => (
              <div
                key={`history-${item.id}`}
                style={{
                  marginBottom: 12,
                  padding: "13px 14px",
                  borderRadius: 18,
                  border: `1px solid ${
                    item.sender === "user" ? "rgba(255,255,255,0.14)" : themeColors.border
                  }`,
                  background:
                    item.sender === "user"
                      ? "linear-gradient(135deg, rgba(255,255,255,0.075), rgba(255,255,255,0.025))"
                      : "linear-gradient(135deg, rgba(0,255,255,0.085), rgba(0,80,255,0.035))",
                  boxShadow:
                    item.sender === "ava"
                      ? `0 0 18px rgba(0,255,255,0.08)`
                      : "0 0 12px rgba(255,255,255,0.035)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                    marginBottom: 8,
                    fontSize: 11,
                    opacity: 0.76,
                    letterSpacing: "0.09em",
                    textTransform: "uppercase",
                  }}
                >
                                      <span>
                      {item.sender === "user"
                        ? historyCopy.you
                        : historyCopy.ava}
                    </span>
                    <span>
                      {new Date(item.ts).toLocaleTimeString(
                        interfaceCopy.chat.locale,
                        {
                          hour: "numeric",
                          minute: "2-digit",
                        }
                      )}
                    </span>
                </div>
                <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.55, fontSize: 14 }}>
                  {item.text}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
